// src/components/operationCenter/widgets/GenericMetricCardWidget.tsx
// Shows 1-4 stat cards (big value + change %) fetched from any endpoint.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Settings, TrendingUp, TrendingDown } from "lucide-react";
import type { WidgetProps, GenericMetricCardConfig } from "@/types/dashboard";
import type { ValueFormat } from "./shared/genericWidgetUtils";
import {
  fetchEndpoint,
  extractSingle,
  parseParams,
  paramsToString,
  formatValue,
  changeColor,
  formatChange,
} from "./shared/genericWidgetUtils";
import { WIDGET_EMPTY } from "../WidgetWrapper";

const FORMAT_OPTIONS: ValueFormat[] = ["number", "currency", "compact", "percent", "ratio"];

type MetricDef = NonNullable<GenericMetricCardConfig["metrics"]>[number];

const EMPTY_METRIC: MetricDef = { label: "", field: "", change_field: "", format: "number" };

const GenericMetricCardWidget: React.FC<WidgetProps<GenericMetricCardConfig>> = ({
  widget,
  onConfigChange,
}) => {
  const config = widget.config as GenericMetricCardConfig;

  const [showConfig, setShowConfig] = useState(!config.endpoint);
  const [draftEndpoint, setDraftEndpoint] = useState(config.endpoint ?? "");
  const [draftTitle, setDraftTitle] = useState(config.title ?? "Metrics");
  const [draftParams, setDraftParams] = useState(paramsToString(config.params));
  const [draftMetrics, setDraftMetrics] = useState<MetricDef[]>(
    config.metrics?.length ? config.metrics : [{ ...EMPTY_METRIC }],
  );

  useEffect(() => {
    setDraftEndpoint(config.endpoint ?? "");
    setDraftTitle(config.title ?? "Metrics");
    setDraftParams(paramsToString(config.params));
    setDraftMetrics(config.metrics?.length ? config.metrics : [{ ...EMPTY_METRIC }]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget.id]);

  const { data: rawData, isLoading, error, refetch } = useQuery({
    queryKey: ["generic_metric_card", config.endpoint, config.params],
    queryFn: ({ signal }) => fetchEndpoint(config.endpoint, config.params ?? {}, signal),
    enabled: Boolean(config.endpoint),
    staleTime: 60_000,
    retry: 1,
  });

  const record = useMemo(() => (rawData ? extractSingle(rawData) : {}), [rawData]);

  const handleSave = useCallback(() => {
    const metrics = draftMetrics.filter((m) => m.field.trim());
    const params = parseParams(draftParams);
    onConfigChange?.({
      endpoint: draftEndpoint,
      title: draftTitle,
      metrics: metrics.length ? metrics : [EMPTY_METRIC],
      ...(Object.keys(params).length ? { params } : {}),
    });
    setShowConfig(false);
  }, [draftEndpoint, draftTitle, draftParams, draftMetrics, onConfigChange]);

  const handleCancel = useCallback(() => {
    setDraftEndpoint(config.endpoint ?? "");
    setDraftTitle(config.title ?? "Metrics");
    setDraftParams(paramsToString(config.params));
    setDraftMetrics(config.metrics?.length ? config.metrics : [{ ...EMPTY_METRIC }]);
    setShowConfig(false);
  }, [config]);

  const updateMetric = (i: number, patch: Partial<MetricDef>) => {
    setDraftMetrics((prev) => prev.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  };

  const addMetric = () =>
    setDraftMetrics((prev) => (prev.length < 4 ? [...prev, { ...EMPTY_METRIC }] : prev));

  const removeMetric = (i: number) =>
    setDraftMetrics((prev) => prev.filter((_, idx) => idx !== i));

  const metrics = config.metrics?.filter((m) => m.field) ?? [];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium truncate">
          {config.title || "Metric Card"}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => refetch()}
            disabled={!config.endpoint}
            className="btn btn-ghost btn-xs p-1 text-slate-400 hover:text-slate-200"
            title="Refresh"
          >
            <RefreshCw size={12} />
          </button>
          <button
            onClick={() => setShowConfig((s) => !s)}
            className="btn btn-ghost btn-xs p-1 text-slate-400 hover:text-slate-200"
            title="Configure"
          >
            <Settings size={12} />
          </button>
        </div>
      </div>

      {/* Config form */}
      {showConfig && (
        <div className="flex-shrink-0 p-3 border-b border-slate-700/30 bg-gray-900/50 space-y-2 overflow-y-auto max-h-72">
          <div className="flex flex-wrap gap-2">
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Endpoint</label>
              <input
                type="text"
                value={draftEndpoint}
                onChange={(e) => setDraftEndpoint(e.target.value)}
                placeholder="/api/v1/price"
                className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5"
              />
            </div>
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Title</label>
              <input
                type="text"
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                placeholder="Metrics"
                className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wide">Query Params</label>
            <input
              type="text"
              value={draftParams}
              onChange={(e) => setDraftParams(e.target.value)}
              placeholder="token=BTC&currency=usd"
              className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5"
            />
          </div>

          {/* Metrics rows */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Metrics</label>
              {draftMetrics.length < 4 && (
                <button onClick={addMetric} className="text-[10px] text-blue-400 hover:text-blue-300">
                  + Add
                </button>
              )}
            </div>
            <div className="space-y-1.5">
              {draftMetrics.map((m, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1 items-center">
                  <input
                    type="text"
                    value={m.label}
                    onChange={(e) => updateMetric(i, { label: e.target.value })}
                    placeholder="Label"
                    className="input input-xs bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs"
                  />
                  <input
                    type="text"
                    value={m.field}
                    onChange={(e) => updateMetric(i, { field: e.target.value })}
                    placeholder="field"
                    className="input input-xs bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs"
                  />
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={m.change_field ?? ""}
                      onChange={(e) => updateMetric(i, { change_field: e.target.value })}
                      placeholder="chg field"
                      className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs"
                    />
                    <select
                      value={m.format ?? "number"}
                      onChange={(e) => updateMetric(i, { format: e.target.value as ValueFormat })}
                      className="select select-xs bg-gray-800/60 border-gray-700/50 text-slate-200 text-[10px] w-20"
                    >
                      {FORMAT_OPTIONS.map((f) => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </div>
                  {draftMetrics.length > 1 && (
                    <button
                      onClick={() => removeMetric(i)}
                      className="text-rose-500 hover:text-rose-400 text-xs leading-none"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={handleCancel} className="btn btn-xs btn-ghost text-slate-400">
              Cancel
            </button>
            <button onClick={handleSave} className="btn btn-xs btn-primary">
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Metric cards */}
      <div className="flex-1 min-h-0 flex flex-col justify-center p-3">
        {!config.endpoint ? (
          <div className={WIDGET_EMPTY}>
            <div className="text-center space-y-2">
              <p>No endpoint configured</p>
              <button
                onClick={() => setShowConfig(true)}
                className="btn btn-xs btn-outline btn-primary"
              >
                Configure
              </button>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="loading loading-spinner loading-sm text-primary" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-rose-400 text-xs text-center px-4">
            {error instanceof Error ? error.message : "Error loading data"}
          </div>
        ) : (
          <div className="grid gap-3 h-full grid-cols-[repeat(auto-fit,minmax(110px,1fr))]">
            {metrics.map((m, i) => {
              const value = record[m.field];
              const change =
                m.change_field ? (record[m.change_field] as number | null) : null;

              return (
                <div
                  key={i}
                  className="flex flex-col justify-center bg-slate-800/40 border border-slate-700/30 rounded-lg p-3"
                >
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
                    {m.label || m.field}
                  </div>
                  <div className="text-xl font-semibold text-slate-100 tabular-nums truncate">
                    {value != null ? formatValue(value, m.format ?? "number") : "—"}
                  </div>
                  {change != null && (
                    <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${changeColor(change)}`}>
                      {change >= 0
                        ? <TrendingUp size={11} />
                        : <TrendingDown size={11} />}
                      {formatChange(change)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericMetricCardWidget;
